import { type ElementType } from 'react';

import {
  BlocksIcon,
  CropIcon,
  EraserIcon,
  FileStackIcon,
  ImageUpscaleIcon,
  LanguagesIcon,
  MessageSquareIcon,
  PencilIcon,
  RemoveBackgroundIcon,
  RotateCwSquareIcon,
  ScanBoxIcon,
  SlidersVerticalIcon,
  SpotlightIcon,
  SquarePlayIcon,
  WandSparklesIcon,
} from '@air/design-icons';

import { type ImageTransformMode } from '../types';

export const GALLERY_CANVAS_IMAGE_TOOL_COUNT = 4;

/** Always shown in the gallery image submenu; remaining slots rotate. */
export const PERSISTENT_GALLERY_CANVAS_IMAGE_TOOL_ID = 'animate-in-canvas';

/** The "Social variants" tool, which the gallery hides while "Resize for [set name]" is offered. */
export const SMART_RESIZE_CANVAS_IMAGE_TOOL_ID = 'smart-resize-in-canvas';

type ToolIcon = ElementType;

/**
 * Gallery "Edit in canvas" actions that are not a 1:1 `setCanvasMode` call.
 * Image transform modes are launched with `setCanvasMode({ mode, nodeId })`.
 */
export type SpecialCanvasImageEditAction = 'prompt' | 'smartResize' | 'splitElements' | 'translate';

export type CanvasImageEditAction = ImageTransformMode | SpecialCanvasImageEditAction;

export type CanvasImageEditTool = {
  action: CanvasImageEditAction;
  icon: ToolIcon;
  id: string;
  label: string;
};

/** Keep labels, icons, and actions aligned with `ToolsCatalog`. */
export const CANVAS_IMAGE_EDIT_TOOLS: readonly CanvasImageEditTool[] = [
  {
    action: 'editTextOcr',
    icon: PencilIcon,
    id: 'edit-text-in-canvas',
    label: 'Edit text',
  },
  {
    action: 'prompt',
    icon: MessageSquareIcon,
    id: 'prompt-in-canvas',
    label: 'Prompt',
  },
  {
    action: 'translate',
    icon: LanguagesIcon,
    id: 'translate-in-canvas',
    label: 'Translate',
  },
  {
    action: 'cameraAngle',
    icon: ScanBoxIcon,
    id: 'camera-angle-in-canvas',
    label: 'Camera angle',
  },
  {
    action: 'changeLighting',
    icon: SpotlightIcon,
    id: 'change-lighting-in-canvas',
    label: 'Change lighting',
  },
  {
    action: 'removeBackground',
    icon: RemoveBackgroundIcon,
    id: 'remove-background-in-canvas',
    label: 'Remove background',
  },
  {
    action: 'extendBackground',
    icon: ImageUpscaleIcon,
    id: 'extend-background-in-canvas',
    label: 'Extend background',
  },
  {
    action: 'removeObject',
    icon: EraserIcon,
    id: 'erase-in-canvas',
    label: 'Erase',
  },
  {
    action: 'upscale',
    icon: WandSparklesIcon,
    id: 'upscale-in-canvas',
    label: 'Upscale',
  },
  {
    action: 'splitElements',
    icon: BlocksIcon,
    id: 'split-elements-in-canvas',
    label: 'Split elements',
  },
  {
    action: 'adjustImage',
    icon: SlidersVerticalIcon,
    id: 'adjust-image-in-canvas',
    label: 'Adjust image',
  },
  {
    action: 'crop',
    icon: CropIcon,
    id: 'crop-in-canvas',
    label: 'Crop',
  },
  {
    action: 'rotateFlip',
    icon: RotateCwSquareIcon,
    id: 'rotate-flip-in-canvas',
    label: 'Flip & rotate',
  },
  {
    action: 'imageToVideo',
    icon: SquarePlayIcon,
    id: 'animate-in-canvas',
    label: 'Animate',
  },
  {
    action: 'smartResize',
    icon: FileStackIcon,
    id: SMART_RESIZE_CANVAS_IMAGE_TOOL_ID,
    label: 'Social variants',
  },
];

const FNV_OFFSET_BASIS = 2166136261;
const FNV_PRIME = 16777619;

const hashString = (value: string): number => {
  let hash = FNV_OFFSET_BASIS;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, FNV_PRIME);
  }

  return hash >>> 0;
};

const UINT32_RANGE = 0x1_0000_0000;
const LCG_INCREMENT = 0x6d2b79f5;

type SeededRandom = () => number;

const createSeededRandom = (seed: number): SeededRandom => {
  let state = seed >>> 0;

  return () => {
    state = (Math.imul(state, LCG_INCREMENT) + 1) >>> 0;
    return state / UINT32_RANGE;
  };
};

const shuffleInPlace = <Item>(items: Item[], random: SeededRandom) => {
  for (let index = items.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    const current = items[index];
    const swap = items[swapIndex];

    if (!current || !swap) {
      continue;
    }

    items[index] = swap;
    items[swapIndex] = current;
  }
};

/** Shuffles a copy of `items`, in the same order every time for the same `seed`. */
export const shuffleWithSeed = <Item>(items: readonly Item[], seed: string): Item[] => {
  const shuffled = [...items];

  shuffleInPlace(shuffled, createSeededRandom(hashString(seed)));

  return shuffled;
};

export type PickRandomCanvasImageEditToolsOptions = {
  count?: number;
  /**
   * Dropped after the shuffle, so the remaining picks are the same tools `seed` gets without
   * exclusions, in the same rotation order.
   */
  excludeIds?: string[];
};

/**
 * Picks `count` catalog tools with a stable shuffle keyed by `seed` (typically a clip id)
 * so the submenu does not reshuffle while the menu is open. Animate is always included
 * (PER-338); the other slots rotate.
 */
export const pickRandomCanvasImageEditTools = (
  seed: string,
  { count = GALLERY_CANVAS_IMAGE_TOOL_COUNT, excludeIds = [] }: PickRandomCanvasImageEditToolsOptions = {},
): CanvasImageEditTool[] => {
  const isIncluded = (tool: CanvasImageEditTool) => !excludeIds.includes(tool.id);
  const persistentTools = CANVAS_IMAGE_EDIT_TOOLS.filter(
    (tool) => tool.id === PERSISTENT_GALLERY_CANVAS_IMAGE_TOOL_ID && isIncluded(tool),
  );
  const shuffledRotatingTools = CANVAS_IMAGE_EDIT_TOOLS.filter(
    (tool) => tool.id !== PERSISTENT_GALLERY_CANVAS_IMAGE_TOOL_ID,
  );
  const random = createSeededRandom(hashString(seed));

  shuffleInPlace(shuffledRotatingTools, random);

  const rotatingTools = shuffledRotatingTools.filter(isIncluded);

  const rotatingCount = Math.max(0, Math.min(count - persistentTools.length, rotatingTools.length));
  const picked = [...persistentTools, ...rotatingTools.slice(0, rotatingCount)];

  shuffleInPlace(picked, random);

  return picked;
};
